import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { JsonLd, ORGANIZATION, SITE_URL } from "@/components/JsonLd";
import type { Language } from "@/types/i18n";

/**
 * /medication-travel-letter — a free doctor's letter for travelling with
 * medication, in up to five languages, with no account (MAR-014 §2, cluster
 * `medication-letter-templates`).
 *
 * The research of 3 November 2026 found that below the CDC's own template the
 * results for "medication travel letter template free" are spam template farms
 * and editors behind a sign-up, and that the German "Medikamentenplan auf
 * Englisch" has no template at all. This page is that template.
 *
 * The traveller prepares the letter; their doctor checks, signs and stamps it.
 * The page says so on the letter itself, because a letter that looks finished
 * without a signature is worth nothing at a border.
 *
 * As on the emergency card, nothing typed here leaves the browser.
 */

const LANGUAGES: { code: Language; name: string }[] = [
  { code: "en", name: "English" },
  { code: "de", name: "German" },
  { code: "fr", name: "French" },
  { code: "es", name: "Spanish" },
  { code: "pt", name: "Portuguese" },
];

type Term = Record<Language, string>;

const TEXT = {
  title: {
    en: "Medical certificate for travelling with medication",
    de: "Ärztliche Bescheinigung für die Mitnahme von Medikamenten auf Reisen",
    fr: "Certificat médical pour voyager avec des médicaments",
    es: "Certificado médico para viajar con medicamentos",
    pt: "Atestado médico para viajar com medicamentos",
  },
  salutation: {
    en: "To whom it may concern,",
    de: "Sehr geehrte Damen und Herren,",
    fr: "À qui de droit,",
    es: "A quien corresponda:",
    pt: "A quem possa interessar,",
  },
  name: {
    en: "Name",
    de: "Name",
    fr: "Nom",
    es: "Nombre",
    pt: "Nome",
  },
  born: {
    en: "Date of birth",
    de: "Geburtsdatum",
    fr: "Date de naissance",
    es: "Fecha de nacimiento",
    pt: "Data de nascimento",
  },
  passport: {
    en: "Passport number",
    de: "Reisepassnummer",
    fr: "Numéro de passeport",
    es: "Número de pasaporte",
    pt: "Número do passaporte",
  },
  journey: {
    en: "Journey",
    de: "Reise",
    fr: "Voyage",
    es: "Viaje",
    pt: "Viagem",
  },
  diagnosis: {
    en: "Diagnosis",
    de: "Diagnose",
    fr: "Diagnostic",
    es: "Diagnóstico",
    pt: "Diagnóstico",
  },
  care: {
    en: "I confirm that the person named above is under my medical care.",
    de: "Ich bestätige, dass sich die oben genannte Person in meiner ärztlichen Behandlung befindet.",
    fr: "Je certifie que la personne mentionnée ci-dessus est sous mes soins médicaux.",
    es: "Certifico que la persona arriba indicada está bajo mi atención médica.",
    pt: "Declaro que a pessoa acima identificada se encontra sob os meus cuidados médicos.",
  },
  need: {
    en: "The medicines listed below have been prescribed for personal use and are needed during the journey. The quantities carried do not exceed what is needed for the duration of the stay.",
    de: "Die unten aufgeführten Medikamente wurden für den persönlichen Gebrauch verordnet und werden während der Reise benötigt. Die mitgeführten Mengen überschreiten nicht den Bedarf für die Dauer des Aufenthalts.",
    fr: "Les médicaments ci-dessous ont été prescrits pour un usage personnel et sont nécessaires pendant le voyage. Les quantités transportées ne dépassent pas les besoins pour la durée du séjour.",
    es: "Los medicamentos indicados a continuación han sido recetados para uso personal y son necesarios durante el viaje. Las cantidades transportadas no superan lo necesario para la duración de la estancia.",
    pt: "Os medicamentos indicados abaixo foram receitados para uso pessoal e são necessários durante a viagem. As quantidades transportadas não excedem o necessário para a duração da estadia.",
  },
  syringes: {
    en: "To take this medication, the person also needs to carry syringes, needles or injection pens.",
    de: "Zur Verabreichung dieser Medikamente muss die Person außerdem Spritzen, Kanülen oder Injektionspens mitführen.",
    fr: "Pour administrer ce traitement, la personne doit également transporter des seringues, des aiguilles ou des stylos injecteurs.",
    es: "Para administrar esta medicación, la persona también necesita llevar jeringas, agujas o plumas de inyección.",
    pt: "Para administrar esta medicação, a pessoa precisa também de transportar seringas, agulhas ou canetas injetoras.",
  },
  liquids: {
    en: "Some of these medicines are liquids or gels in containers over 100 ml and must be carried in the hand luggage.",
    de: "Einige dieser Medikamente sind Flüssigkeiten oder Gele in Behältern über 100 ml und müssen im Handgepäck mitgeführt werden.",
    fr: "Certains de ces médicaments sont des liquides ou des gels en contenants de plus de 100 ml et doivent être transportés en bagage à main.",
    es: "Algunos de estos medicamentos son líquidos o geles en envases de más de 100 ml y deben llevarse en el equipaje de mano.",
    pt: "Alguns destes medicamentos são líquidos ou géis em embalagens com mais de 100 ml e têm de ser transportados na bagagem de mão.",
  },
  medicine: {
    en: "Medicine (generic name)",
    de: "Wirkstoff",
    fr: "Médicament (DCI)",
    es: "Medicamento (DCI)",
    pt: "Medicamento (DCI)",
  },
  strength: {
    en: "Strength and form",
    de: "Stärke und Darreichungsform",
    fr: "Dosage et forme",
    es: "Concentración y forma",
    pt: "Dosagem e forma",
  },
  dose: {
    en: "Dose",
    de: "Dosierung",
    fr: "Posologie",
    es: "Dosis",
    pt: "Posologia",
  },
  quantity: {
    en: "Quantity carried",
    de: "Mitgeführte Menge",
    fr: "Quantité transportée",
    es: "Cantidad transportada",
    pt: "Quantidade transportada",
  },
  questions: {
    en: "Please contact me if you have any questions.",
    de: "Für Rückfragen stehe ich gerne zur Verfügung.",
    fr: "Je reste à votre disposition pour toute question.",
    es: "Quedo a su disposición para cualquier consulta.",
    pt: "Fico ao dispor para qualquer esclarecimento.",
  },
  signature: {
    en: "Doctor's name, signature and stamp",
    de: "Name, Unterschrift und Stempel der Ärztin oder des Arztes",
    fr: "Nom, signature et cachet du médecin",
    es: "Nombre, firma y sello del médico",
    pt: "Nome, assinatura e carimbo do médico",
  },
  date: {
    en: "Date",
    de: "Datum",
    fr: "Date",
    es: "Fecha",
    pt: "Data",
  },
} satisfies Record<string, Term>;

/** A term in every chosen language, each spelling once ("Date", not "Date · Date"). */
function inLanguages(term: Term, languages: Language[]): string {
  return [...new Set(languages.map((code) => term[code]))].join(" · ");
}

interface Medicine {
  name: string;
  strength: string;
  dose: string;
  quantity: string;
}

interface LetterState {
  name: string;
  born: string;
  passport: string;
  journey: string;
  diagnosis: string;
  medicines: Medicine[];
  syringes: boolean;
  liquids: boolean;
  doctor: string;
  practice: string;
  languages: Language[];
}

const BLANK_MEDICINE: Medicine = {
  name: "",
  strength: "",
  dose: "",
  quantity: "",
};

const EMPTY: LetterState = {
  name: "",
  born: "",
  passport: "",
  journey: "",
  diagnosis: "",
  medicines: [{ ...BLANK_MEDICINE }],
  syringes: false,
  liquids: false,
  doctor: "",
  practice: "",
  languages: ["en", "es"],
};

const toggle = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const FAQS = [
  {
    question: "Is a letter I make here valid without my doctor?",
    answer:
      "No. This page prepares the letter; your doctor checks it, signs it and stamps it. A border officer or pharmacist relies on the signature, not on the template. Bring the filled-in letter to your appointment, so the doctor only has to check and sign.",
  },
  {
    question: "What should a doctor's letter for travel medication say?",
    answer:
      "Your name and date of birth, your diagnosis, and every medicine with its generic name, strength, dose and the quantity you carry, plus a statement that it is for personal use. It should name any syringes or devices, and carry the doctor's name, contact details, signature and the date. The CDC and the NHS advise the same.",
  },
  {
    question: "Why generic names?",
    answer:
      "Brand names differ from country to country; the international non-proprietary name (INN), such as metformin or levetiracetam, is recognised everywhere. Customs officers and pharmacists abroad look for it.",
  },
  {
    question: "Is this enough for controlled medicines?",
    answer:
      "Often not. Strong painkillers such as opioids, benzodiazepines, ADHD stimulants and some sleeping pills are controlled. Within the Schengen area you need the Schengen certificate (Article 75) issued by your health authority; other countries may require a permit before you travel. Check the destination's rules with the INCB country list and the destination's embassy.",
  },
  {
    question: "Is what I type stored anywhere?",
    answer:
      "No. The letter is put together in your browser. Nothing you enter is sent to HealthPass or anyone else, and closing the page forgets it.",
  },
];

export function MedicationLetterToolPage() {
  const [letter, setLetter] = useState<LetterState>(EMPTY);
  const set = <K extends keyof LetterState>(key: K, value: LetterState[K]) =>
    setLetter((current) => ({ ...current, [key]: value }));
  const setMedicine = (index: number, key: keyof Medicine, value: string) =>
    set(
      "medicines",
      letter.medicines.map((medicine, i) =>
        i === index ? { ...medicine, [key]: value } : medicine,
      ),
    );

  const langs = useMemo(
    () =>
      LANGUAGES.map((l) => l.code).filter((code) =>
        letter.languages.includes(code),
      ),
    [letter.languages],
  );
  const label = (term: Term) => inLanguages(term, langs);
  // Whole sentences stacked one per language, each in its own paragraph: a
  // sentence joined with " · " reads as one long line nobody can follow.
  const sentences = (term: Term) =>
    [...new Set(langs.map((code) => term[code]))].map((text, i) => (
      <p key={text} className={i > 0 ? "italic text-gray-600" : undefined}>
        {text}
      </p>
    ));

  const medicines = letter.medicines.filter((m) => m.name.trim());

  const input =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Free doctor's letter for travelling with medication, in five languages"
        description="Prepare a doctor's letter for travelling with medication in English, German, French, Spanish or Portuguese: diagnosis, generic names, doses and quantities. Your doctor signs it. No sign-up; nothing you type leaves your browser."
        keywords="medication travel letter template, doctor's letter for travelling with medication, medical certificate for travel medication, Medikamentenplan auf Englisch, carta médica para viajar con medicamentos"
        url={`${SITE_URL}/medication-travel-letter`}
        canonical={`${SITE_URL}/medication-travel-letter`}
      />
      <JsonLd
        data={{
          "@type": "WebApplication",
          name: "Free doctor's letter for travelling with medication",
          url: `${SITE_URL}/medication-travel-letter`,
          applicationCategory: "HealthApplication",
          operatingSystem: "Any (web browser)",
          inLanguage: ["en", "de", "fr", "es", "pt"],
          description:
            "A printable doctor's letter for travelling with medication in up to five languages, made in the browser with no account, for the doctor to sign.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@id": ORGANIZATION["@id"] },
        }}
      />
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />
      {/* Only the letter goes to the printer, whatever the app shell around it. */}
      <style>{`@media print {
  body * { visibility: hidden; }
  #medication-letter, #medication-letter * { visibility: visible; }
  #medication-letter { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; border: none; }
}`}</style>

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-14 pt-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Free doctor&apos;s letter for travelling with medication
          </h1>
          <p className="text-lg md:text-xl text-blue-100">
            Fill in your diagnosis and medicines with their generic names, doses
            and quantities, choose the languages, and print a letter for your
            doctor to sign. English, German, French, Spanish and Portuguese, in
            any combination. No sign-up, and nothing you type leaves your
            browser.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-10 lg:grid-cols-2">
        <form
          className="space-y-6"
          onSubmit={(event) => event.preventDefault()}
          aria-label="Letter details"
        >
          <fieldset>
            <legend className="font-semibold text-gray-900 mb-2">
              Letter languages
            </legend>
            <div className="flex flex-wrap gap-3">
              {LANGUAGES.map(({ code, name }) => (
                <label key={code} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={letter.languages.includes(code)}
                    onChange={() =>
                      set(
                        "languages",
                        letter.languages.includes(code) &&
                          letter.languages.length === 1
                          ? letter.languages
                          : toggle(letter.languages, code),
                      )
                    }
                  />
                  {name}
                </label>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Pick your doctor&apos;s language and the languages of the
              countries on your route.
            </p>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Full name
              </span>
              <input
                className={input}
                value={letter.name}
                onChange={(e) => set("name", e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Date of birth
              </span>
              <input
                className={input}
                value={letter.born}
                onChange={(e) => set("born", e.target.value)}
                placeholder="e.g. 14 Mar 1958"
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Passport number (optional)
              </span>
              <input
                className={input}
                value={letter.passport}
                onChange={(e) => set("passport", e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Destination and dates
              </span>
              <input
                className={input}
                value={letter.journey}
                onChange={(e) => set("journey", e.target.value)}
                placeholder="e.g. Spain, 2–16 June 2027"
                autoComplete="off"
              />
            </label>
          </div>

          <label className="block">
            <span className="font-semibold text-gray-900">Diagnosis</span>
            <span className="block text-sm text-gray-600 mb-2">
              As your doctor would write it, e.g. Type 1 diabetes mellitus.
            </span>
            <input
              className={input}
              value={letter.diagnosis}
              onChange={(e) => set("diagnosis", e.target.value)}
              autoComplete="off"
            />
          </label>

          <fieldset>
            <legend className="font-semibold text-gray-900 mb-2">
              Medicines
            </legend>
            <p className="text-sm text-gray-600 mb-3">
              Use the generic name (e.g. insulin glargine, not the brand), as
              printed on the box.
            </p>
            <div className="space-y-4">
              {letter.medicines.map((medicine, i) => (
                <div
                  key={i}
                  className="grid gap-2 sm:grid-cols-2 rounded-md border border-gray-200 bg-white p-3"
                >
                  <input
                    className={input}
                    value={medicine.name}
                    onChange={(e) => setMedicine(i, "name", e.target.value)}
                    placeholder="Generic name"
                    aria-label={`Medicine ${i + 1}: generic name`}
                    autoComplete="off"
                  />
                  <input
                    className={input}
                    value={medicine.strength}
                    onChange={(e) => setMedicine(i, "strength", e.target.value)}
                    placeholder="Strength and form, e.g. 100 U/ml pen"
                    aria-label={`Medicine ${i + 1}: strength and form`}
                    autoComplete="off"
                  />
                  <input
                    className={input}
                    value={medicine.dose}
                    onChange={(e) => setMedicine(i, "dose", e.target.value)}
                    placeholder="Dose, e.g. 20 units once daily"
                    aria-label={`Medicine ${i + 1}: dose`}
                    autoComplete="off"
                  />
                  <input
                    className={input}
                    value={medicine.quantity}
                    onChange={(e) => setMedicine(i, "quantity", e.target.value)}
                    placeholder="Quantity carried, e.g. 3 pens"
                    aria-label={`Medicine ${i + 1}: quantity carried`}
                    autoComplete="off"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                set("medicines", [...letter.medicines, { ...BLANK_MEDICINE }])
              }
              className="mt-3 text-blue-700 font-semibold hover:underline"
            >
              + Add a medicine
            </button>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="font-semibold text-gray-900 mb-2">
              Also needed
            </legend>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={letter.syringes}
                onChange={() => set("syringes", !letter.syringes)}
              />
              Syringes, needles or injection pens
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={letter.liquids}
                onChange={() => set("liquids", !letter.liquids)}
              />
              Liquids or gels over 100 ml in the hand luggage
            </label>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Doctor&apos;s name (optional)
              </span>
              <input
                className={input}
                value={letter.doctor}
                onChange={(e) => set("doctor", e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Practice, address and phone (optional)
              </span>
              <input
                className={input}
                value={letter.practice}
                onChange={(e) => set("practice", e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
        </form>

        <div>
          <div
            id="medication-letter"
            className="bg-white rounded-xl border border-gray-300 shadow-sm p-6 text-sm text-gray-900 space-y-4"
          >
            <p className="text-base font-bold">{label(TEXT.title)}</p>
            <div>{sentences(TEXT.salutation)}</div>
            <dl className="space-y-1">
              {(
                [
                  [TEXT.name, letter.name],
                  [TEXT.born, letter.born],
                  [TEXT.passport, letter.passport],
                  [TEXT.journey, letter.journey],
                  [TEXT.diagnosis, letter.diagnosis],
                ] as const
              )
                .filter(([, value]) => value.trim())
                .map(([term, value]) => (
                  <div key={term.en}>
                    <dt className="font-semibold inline">{label(term)}: </dt>
                    <dd className="inline">{value}</dd>
                  </div>
                ))}
            </dl>
            <div>{sentences(TEXT.care)}</div>
            <div>{sentences(TEXT.need)}</div>
            {medicines.length > 0 && (
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    {[
                      TEXT.medicine,
                      TEXT.strength,
                      TEXT.dose,
                      TEXT.quantity,
                    ].map((term) => (
                      <th
                        key={term.en}
                        className="border border-gray-300 p-1 text-left align-top"
                      >
                        {label(term)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {medicines.map((medicine, i) => (
                    <tr key={i}>
                      <td className="border border-gray-300 p-1">
                        {medicine.name}
                      </td>
                      <td className="border border-gray-300 p-1">
                        {medicine.strength}
                      </td>
                      <td className="border border-gray-300 p-1">
                        {medicine.dose}
                      </td>
                      <td className="border border-gray-300 p-1">
                        {medicine.quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {letter.syringes && <div>{sentences(TEXT.syringes)}</div>}
            {letter.liquids && <div>{sentences(TEXT.liquids)}</div>}
            <div>{sentences(TEXT.questions)}</div>
            <div className="pt-6 grid grid-cols-2 gap-6">
              <div>
                <div className="border-b border-gray-400 h-14" />
                <p className="mt-1 text-xs">{label(TEXT.signature)}</p>
                {letter.doctor && <p className="text-xs">{letter.doctor}</p>}
                {letter.practice && (
                  <p className="text-xs">{letter.practice}</p>
                )}
              </div>
              <div>
                <div className="border-b border-gray-400 h-14" />
                <p className="mt-1 text-xs">{label(TEXT.date)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              travelhealthpass.app/medication-travel-letter
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="mt-4 w-full bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700 transition"
          >
            Print the letter
          </button>
          <p className="mt-2 text-sm text-gray-600">
            Take it to your doctor to check, sign and stamp. Carry the signed
            letter in your hand luggage with the medicines in their original
            packaging.
          </p>
          <div className="mt-6 rounded-lg bg-blue-50 border border-blue-200 p-4 text-gray-800">
            <p className="font-semibold">Keep your medicines with you abroad</p>
            <p className="mt-1">
              HealthPass keeps your medicines, allergies and conditions on your
              phone behind a QR code that a doctor abroad reads in their
              language, and you can also print a{" "}
              <Link
                to="/emergency-medical-card"
                className="text-blue-700 font-semibold hover:underline"
              >
                free translated emergency card
              </Link>
              .
            </p>
            <Link
              to="/traveler/register"
              className="inline-block mt-3 text-blue-700 font-semibold hover:underline"
            >
              Create your free QR emergency card
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            When you need a doctor&apos;s letter
          </h2>
          <p className="text-gray-700">
            Carry one whenever you travel with prescription medicine, and always
            with injectables, liquids over 100 ml or anything controlled. It
            tells a security officer, a customs officer or a pharmacist abroad
            what the medicine is, that it is yours and why you carry that much.
            The{" "}
            <a
              href="https://www.cdc.gov/yellow-book/hcp/travelers-with-additional-considerations/travelers-with-chronic-illnesses.html"
              className="text-blue-700 hover:underline"
            >
              CDC&apos;s advice for travellers with a chronic illness
            </a>{" "}
            and the{" "}
            <a
              href="https://www.nhs.uk/common-health-questions/medicines/can-i-take-my-medicine-abroad/"
              className="text-blue-700 hover:underline"
            >
              NHS guidance on taking medicine abroad
            </a>{" "}
            recommend the same.
          </p>
          <p className="text-gray-700 mt-3">
            Controlled medicines need more than a letter in many countries.
            Check the destination in the{" "}
            <a
              href="https://www.incb.org/incb/en/travellers/country-regulations.html"
              className="text-blue-700 hover:underline"
            >
              INCB&apos;s country regulations for travellers
            </a>{" "}
            before you book.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-lg font-semibold text-gray-900">
                  {faq.question}
                </h3>
                <p className="text-gray-700 mt-1">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
